import React, { useEffect, useMemo, useRef, useState } from "react";
import "../Style/StatusDropdown.css";

export default function CustomDropdown({
  multi = false,
  value,
  onChange,
  options = [],
  placeholder = "Select",
  leftIcon = null,
  rightIcon = null,
  className = "",
  openClassName = "",
  menuClassName = "",
  optionClassName = "",
  disabled = false,
  searchable = false,
  renderTrigger = null,
  renderMenu = null,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const wrapRef = useRef(null);
  const searchRef = useRef(null);

  const normalized = useMemo(
    () =>
      (options || []).map((opt) =>
        typeof opt === "string" ? { label: opt, value: opt } : opt,
      ),
    [options],
  );

  const filtered = useMemo(() => {
    if (!searchable) return normalized;

    const q = search.trim().toLowerCase();
    if (!q) return normalized;

    return normalized.filter((o) =>
      String(o.label ?? "")
        .toLowerCase()
        .includes(q),
    );
  }, [normalized, search, searchable]);

  useEffect(() => {
    if (!open) return;

    if (searchable) {
      const t = setTimeout(() => searchRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [open, searchable]);

  useEffect(() => {
    if (!open) return;

    const onOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setSearch("");
      }
    };

    const onEsc = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        setSearch("");
      }
    };

    document.addEventListener("mousedown", onOutside);
    document.addEventListener("keydown", onEsc);

    return () => {
      document.removeEventListener("mousedown", onOutside);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const isMulti = multi && Array.isArray(value);
  const safeValue = Array.isArray(value) ? value : [];

  const selectedLabel = isMulti
    ? safeValue
        .map((v) => normalized.find((o) => o.value === v)?.label)
        .filter(Boolean)
        .join(", ")
    : (normalized.find((o) => o.value === value)?.label ?? "");

  return (
    <div
      ref={wrapRef}
      className={`custom-dropdown ${className} ${
        open ? `is-open ${openClassName}` : ""
      } ${disabled ? "is-disabled" : ""}`.trim()}
      onClick={(event) => {
        if (disabled) return;
        if (event.target.closest(".custom-dropdown-menu")) return;

        setOpen((p) => {
          const next = !p;
          if (!next) setSearch("");
          return next;
        });
      }}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-expanded={open}
      aria-haspopup="listbox"
      aria-disabled={disabled}
      onKeyDown={(event) => {
        if (disabled) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          setOpen((current) => !current);
        }
        if (event.key === "ArrowDown") {
          event.preventDefault();
          setOpen(true);
        }
      }}
    >
      {renderTrigger ? (
        renderTrigger({ open, selectedLabel, placeholder, disabled })
      ) : (
        <>
          {leftIcon && (
            <div className="custom-dropdown-left-icon">{leftIcon}</div>
          )}

          <input
            className="custom-dropdown-input"
            placeholder={placeholder}
            value={selectedLabel}
            readOnly
            disabled={disabled}
            tabIndex={-1}
          />

          <div className="custom-dropdown-right-icon" aria-hidden="true">
            {rightIcon || <span className="custom-dropdown-chevron" />}
          </div>
        </>
      )}

      {open && (
        <div
          className={`custom-dropdown-menu ${menuClassName}`.trim()}
          onClick={(e) => e.stopPropagation()}
          role="listbox"
          aria-multiselectable={isMulti || undefined}
        >
          {renderMenu ? (
            renderMenu({
              open,
              closeMenu: () => {
                setOpen(false);
                setSearch("");
              },
            })
          ) : (
            <>
              {searchable && (
                <input
                  ref={searchRef}
                  type="text"
                  className="custom-dropdown-search"
                  value={search}
                  placeholder="Search..."
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                />
              )}

              {filtered.length
                ? filtered.map((opt, idx) => {
                    const isSelected = isMulti
                      ? safeValue.includes(opt.value)
                      : value === opt.value;

                    return (
                      <div
                        key={`${opt.value}-${idx}`}
                        className={`custom-dropdown-option ${optionClassName} ${
                          isSelected ? "selected" : ""
                        }`}
                        role="option"
                        aria-selected={isSelected}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();

                          if (isMulti) {
                            const newValue = safeValue.includes(opt.value)
                              ? safeValue.filter((v) => v !== opt.value)
                              : [...safeValue, opt.value];

                            onChange?.(newValue);
                          } else {
                            setOpen(false);
                            setSearch("");
                            onChange?.(opt.value);
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {isMulti && (
                          <input
                            type="checkbox"
                            checked={isSelected}
                            readOnly
                            className="custom-dropdown-checkbox"
                          />
                        )}

                        <span className="custom-dropdown-option-label">
                          {opt.label}
                        </span>
                        {isSelected && (
                          <span
                            className="custom-dropdown-selected-mark"
                            aria-hidden="true"
                          >
                            &#10003;
                          </span>
                        )}
                      </div>
                    );
                  })
                : searchable && (
                    <div className="custom-dropdown-empty">No results</div>
                  )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
